import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { FloppyDisk, CheckCircle, Plus, Trash, PencilSimple, MagnifyingGlass, X, Files } from "@phosphor-icons/react";

const STATUSES = ["draft", "review", "approved", "obsolete"];
const CATEGORIES = ["SOP", "WI", "Form", "Manual", "Record", "Other"];

const EMPTY = { code: "", title: "", doc_version: "1.0", category: "SOP", status: "draft", owner_name: "", effective_date: "", notes: "" };

const ST_STYLE = {
  draft: "bg-zinc-50 text-zinc-600 border-zinc-200",
  review: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  obsolete: "bg-red-50 text-red-700 border-red-200",
};

export default function DocControlTab() {
  const t = useT();
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/doccontrol");
    setDocs(r.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: docs.length,
    approved: docs.filter((d) => d.status === "approved").length,
    review: docs.filter((d) => d.status === "review").length,
  }), [docs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (fStatus && d.status !== fStatus) return false;
      if (q && !`${d.code} ${d.title} ${d.category} ${d.owner_name}`.toLowerCase().includes(q)) return false;
      const documentDate = d.effective_date || d.created_at?.slice(0, 10) || "";
      if (dateFrom && (!documentDate || documentDate < dateFrom)) return false;
      if (dateTo && (!documentDate || documentDate > dateTo)) return false;
      return true;
    });
  }, [docs, search, fStatus, dateFrom, dateTo]);

  function showAllDocuments() {
    setSearch("");
    setFStatus("");
    setDateFrom("");
    setDateTo("");
  }

  async function save() {
    if (!draft.code.trim() || !draft.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => { payload[k] = draft[k] || EMPTY[k]; });
      if (editing === "new") await api.post("/doccontrol", payload);
      else await api.put(`/doccontrol/${editing}`, payload);
      setOkMsg(t("qms.saved"));
      setEditing(null);
      await load();
    } finally { setSaving(false); }
  }

  async function remove(doc) {
    if (!window.confirm(t("qms.confirm_delete"))) return;
    await api.delete(`/doccontrol/${doc.id}`);
    if (editing === doc.id) setEditing(null);
    await load();
  }

  return (
    <div className="space-y-5" data-testid="docctrl-tab">
      <div className="grid-card p-5">
        <div className="inline-flex items-center gap-2">
          <Files size={20} weight="duotone" className="text-blue-700" />
          <h2 className="text-lg font-extrabold tracking-tight">{t("docctrl.title")}</h2>
        </div>
        <p className="text-sm text-zinc-600 mt-1">{t("docctrl.sub")}</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label={t("qms.total")} value={stats.total} testid="docctrl-stat-total" />
        <Stat label={t("docctrl.st.approved")} value={stats.approved} accent="text-emerald-700" testid="docctrl-stat-approved" />
        <Stat label={t("docctrl.st.review")} value={stats.review} accent="text-amber-700" testid="docctrl-stat-review" />
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="docctrl-filters">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("docctrl.search")}
            data-testid="docctrl-search"
            className="w-full border border-zinc-200 pl-8 pr-2 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
        </div>
        <label className="flex flex-col gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          {t("docctrl.filter_from")}
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="docctrl-filter-date-from"
            className="border border-zinc-200 px-2 py-2 rounded-sm text-xs normal-case tracking-normal text-zinc-800 bg-white focus:border-blue-600 focus:outline-none" />
        </label>
        <label className="flex flex-col gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          {t("docctrl.filter_to")}
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="docctrl-filter-date-to"
            className="border border-zinc-200 px-2 py-2 rounded-sm text-xs normal-case tracking-normal text-zinc-800 bg-white focus:border-blue-600 focus:outline-none" />
        </label>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="docctrl-filter-status"
          className="border border-zinc-200 px-2 py-2 rounded-sm text-xs bg-white focus:border-blue-600 focus:outline-none">
          <option value="">{t("docctrl.all_docs")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`docctrl.st.${s}`)}</option>)}
        </select>
        <button type="button" onClick={showAllDocuments} data-testid="docctrl-show-all"
          className="border border-zinc-200 hover:bg-zinc-50 px-3 py-2 rounded-sm text-xs font-semibold transition-colors">
          {t("docctrl.show_all")}
        </button>
        <button onClick={() => { setEditing("new"); setDraft(EMPTY); setOkMsg(""); }} data-testid="docctrl-new"
          className="btn-primary inline-flex items-center gap-1 px-3 py-2 text-xs">
          <Plus size={12} weight="bold" /> {t("docctrl.new")}
        </button>
      </div>
      <p className="text-xs text-zinc-500" data-testid="docctrl-date-hint">{t("docctrl.date_filter_hint")}</p>

      {okMsg && (
        <div className="inline-flex items-center gap-1 text-sm text-emerald-700" data-testid="docctrl-ok">
          <CheckCircle size={14} weight="fill" /> {okMsg}
        </div>
      )}

      {editing !== null && (
        <div className="grid-card p-4 space-y-4 border-l-4 border-l-blue-600" data-testid="docctrl-editor">
          <div className="flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
              {editing === "new" ? t("docctrl.new") : `${t("qms.edit")} · ${draft.code}`}
            </div>
            <button onClick={() => setEditing(null)} data-testid="docctrl-editor-close" className="p-1 hover:bg-zinc-100 rounded-sm"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label={t("docctrl.code")} value={draft.code} onChange={(v) => setDraft({ ...draft, code: v })} testid="docctrl-f-code" />
            <div className="md:col-span-2">
              <Field label={t("qms.rec_title")} value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid="docctrl-f-title" />
            </div>
            <Field label={t("docctrl.version")} value={draft.doc_version} onChange={(v) => setDraft({ ...draft, doc_version: v })} testid="docctrl-f-version" />
            <Select label={t("docctrl.category")} value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })}
              options={CATEGORIES.map((c) => [c, c])} testid="docctrl-f-category" />
            <Select label={t("qms.status")} value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })}
              options={STATUSES.map((s) => [s, t(`docctrl.st.${s}`)])} testid="docctrl-f-status" />
            <Field label={t("docctrl.owner")} value={draft.owner_name} onChange={(v) => setDraft({ ...draft, owner_name: v })} testid="docctrl-f-owner" />
            <Field label={t("docctrl.effective")} type="date" value={draft.effective_date} onChange={(v) => setDraft({ ...draft, effective_date: v })} testid="docctrl-f-effective" />
            <div className="md:col-span-3">
              <Field label={t("docctrl.notes")} value={draft.notes} onChange={(v) => setDraft({ ...draft, notes: v })} testid="docctrl-f-notes" />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
            <button onClick={save} disabled={saving || !draft.code.trim() || !draft.title.trim()} data-testid="docctrl-save"
              className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40">
              <FloppyDisk size={14} weight="bold" /> {saving ? "…" : t("qms.save")}
            </button>
            <button onClick={() => setEditing(null)} data-testid="docctrl-cancel"
              className="border border-zinc-200 hover:bg-zinc-50 px-4 py-2 rounded-sm text-sm font-semibold transition-colors">
              {t("qms.cancel")}
            </button>
          </div>
        </div>
      )}

      <div className="grid-card p-0 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="text-xs text-zinc-500 font-mono py-8 text-center" data-testid="docctrl-empty">{t("qms.no_records")}</div>
        ) : (
          <table className="w-full text-sm" data-testid="docctrl-list">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <Th>{t("docctrl.code")}</Th>
                <Th>{t("qms.rec_title")}</Th>
                <Th>{t("docctrl.category")}</Th>
                <Th>{t("docctrl.version")}</Th>
                <Th>{t("qms.status")}</Th>
                <Th>{t("docctrl.owner")}</Th>
                  <Th>{t("docctrl.date")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`docctrl-row-${d.code}`}>
                  <td className="px-3 py-2 font-mono text-xs font-bold">{d.code}</td>
                  <td className="px-3 py-2">{d.title}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.category}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.doc_version}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block border rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ST_STYLE[d.status] || ""}`}>
                      {t(`docctrl.st.${d.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{d.owner_name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.effective_date || d.created_at?.slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditing(d.id); setDraft({ ...EMPTY, ...d }); setOkMsg(""); }} title={t("qms.edit")} data-testid={`docctrl-edit-${d.code}`}
                        className="p-1.5 hover:bg-blue-100 text-blue-700 rounded-sm"><PencilSimple size={14} weight="bold" /></button>
                      <button onClick={() => remove(d)} title={t("qms.delete")} data-testid={`docctrl-del-${d.code}`}
                        className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash size={14} weight="bold" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-zinc-900", testid }) {
  return (
    <div className="grid-card p-3" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-extrabold ${accent}`}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 font-semibold">{children}</th>;
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
